import { useState } from 'react';
import { Avatar, Box, Button, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import moment from 'moment';
import { useAuth } from 'oidc-react';
import { useMutation, useQuery } from '@apollo/client/react';
import { ADD_COMMENT, GET_COMMENTS, REMOVE_COMMENT, UPDATE_COMMENT } from './Comments.graphql';
import { formatDate } from '../../Formatters';
import { tokens } from './tokens';
import LoadingSpinner from '../../components/LoadingSpinner';

interface Comment {
  id: string;
  comment: string;
  createdByName: string;
  createdAt: string;
  lastUpdatedByName: string;
  lastUpdatedAt: string;
  isLocked: boolean;
}

interface Props {
  userId: number;
}

export function CommentsPanel({ userId }: Props) {
  const auth = useAuth();
  const meName = [auth.userData?.profile?.given_name, auth.userData?.profile?.family_name].filter(Boolean).join(' ');

  const [editingId, setEditingId] = useState<string | null>(null);

  const { loading, error, data } = useQuery<{ comments: Comment[] }>(GET_COMMENTS, { variables: { userId } });

  const [addComment] = useMutation<{ userAddComment: Comment }>(ADD_COMMENT, {
    update(cache, { data: result }) {
      if (!result) return;
      const prev = cache.readQuery<{ comments: Comment[] }>({ query: GET_COMMENTS, variables: { userId } });
      if (!prev) return;
      cache.writeQuery({ query: GET_COMMENTS, variables: { userId }, data: { comments: [...prev.comments, result.userAddComment] } });
    },
  });
  const [updateComment] = useMutation<any, any>(UPDATE_COMMENT);
  const [removeComment] = useMutation<any, any>(REMOVE_COMMENT, {
    update(cache, { data: result }) {
      if (!result?.userRemoveComment) return;
      const prev = cache.readQuery<{ comments: Comment[] }>({ query: GET_COMMENTS, variables: { userId } });
      if (!prev) return;
      cache.writeQuery({
        query: GET_COMMENTS,
        variables: { userId },
        data: { comments: prev.comments.filter((c) => c.id !== result.userRemoveComment.id) },
      });
    },
  });

  if (error) return <Box sx={{ p: 3 }}><span>Feil! {error.message}</span></Box>;
  if (loading && !data) return <Box sx={{ p: 3 }}><LoadingSpinner /></Box>;

  const comments = [...(data?.comments ?? [])].sort(sortComments);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <CommentRow avatar={<UserAvatar name={meName || '?'} />}>
        <Composer
          placeholder="Skriv en kommentar …"
          submitLabel="Legg til kommentar"
          onSubmit={(text) => addComment({ variables: { input: { id: userId, comment: text } } })}
        />
      </CommentRow>

      {comments.map((c) => (
        <CommentRow key={c.id} avatar={<UserAvatar name={c.createdByName} />}>
          {editingId === c.id ? (
            <Composer
              initialValue={c.comment}
              submitLabel="Lagre"
              onCancel={() => setEditingId(null)}
              onSubmit={(text) => {
                updateComment({ variables: { input: { id: userId, commentId: c.id, comment: text } } });
                setEditingId(null);
              }}
            />
          ) : (
            <Box
              sx={{
                border: `1px solid ${tokens.border}`,
                borderRadius: `${tokens.radius}px`,
                padding: '12px 16px',
                position: 'relative',
                '&:hover .comment-actions': { opacity: 1 },
              }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: tokens.text }}>{c.createdByName}</Typography>
                <Tooltip title={formatDate(c.createdAt)} placement="top">
                  <Typography sx={{ fontSize: 12, color: tokens.textSecondary, whiteSpace: 'nowrap' }}>
                    {moment(c.createdAt).fromNow()}
                  </Typography>
                </Tooltip>
              </Box>
              <Typography
                component="pre"
                sx={{
                  fontSize: 14,
                  color: tokens.text,
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'inherit',
                  m: 0,
                  mt: 0.5,
                }}>
                {c.comment}
              </Typography>
              {c.lastUpdatedAt && (
                <Typography sx={{ fontSize: 11, color: tokens.textSecondary, mt: 0.5, fontStyle: 'italic' }}>
                  Sist endret av {c.lastUpdatedByName} · {formatDate(c.lastUpdatedAt)}
                </Typography>
              )}
              {!c.isLocked && (
                <Box
                  className="comment-actions"
                  sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    display: 'flex',
                    gap: 0.25,
                    opacity: 0,
                    transition: 'opacity 120ms ease',
                    background: tokens.surface,
                    borderRadius: '6px',
                  }}>
                  <Tooltip title="Endre" placement="top">
                    <IconButton size="small" onClick={() => setEditingId(c.id)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Fjern" placement="top">
                    <IconButton
                      size="small"
                      onClick={() => removeComment({ variables: { input: { id: userId, commentId: c.id } } })}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          )}
        </CommentRow>
      ))}
    </Box>
  );
}

interface ComposerProps {
  placeholder?: string;
  submitLabel: string;
  initialValue?: string;
  onSubmit(text: string): void;
  onCancel?(): void;
}

function Composer({ placeholder, submitLabel, initialValue = '', onSubmit, onCancel }: ComposerProps) {
  const [value, setValue] = useState(initialValue);
  const trimmed = value.trim();
  const canSubmit = trimmed.length > 0 && trimmed !== initialValue.trim();

  const handleCancel = () => {
    setValue(initialValue);
    onCancel?.();
  };
  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit(trimmed);
    setValue(initialValue ? initialValue : '');
  };

  return (
    <Box>
      <TextField
        multiline
        minRows={2}
        fullWidth
        autoFocus={!!initialValue}
        variant="outlined"
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        slotProps={{ input: { sx: { fontSize: 14 } } }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
        <Button
          size="small"
          onClick={handleCancel}
          disabled={!onCancel && value.length === 0}
          sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Avbryt
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={!canSubmit}
          onClick={handleSubmit}
          sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {submitLabel}
        </Button>
      </Box>
    </Box>
  );
}

function CommentRow({ avatar, children }: { avatar: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75, mb: 2 }}>
      <Box sx={{ flexShrink: 0, mt: 0.5 }}>{avatar}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

function UserAvatar({ name }: { name: string }) {
  const bg = colorFor(name);
  return (
    <Avatar
      sx={{
        width: 36,
        height: 36,
        fontSize: 13,
        fontWeight: 600,
        background: bg,
        color: '#fff',
      }}>
      {initials(name)}
    </Avatar>
  );
}

function initials(name: string): string {
  return name
    .split(' ')
    .map((x) => x[0])
    .filter(Boolean)
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

const palette = ['#7B5BC1', '#2E8B57', '#287891', '#C18A3D', '#A33F8C', '#3D6EC1', '#1d5a6e', '#6B7B3F'];

function colorFor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return palette[Math.abs(hash) % palette.length];
}

function sortComments(a: Comment, b: Comment): number {
  const da = a.lastUpdatedAt || a.createdAt;
  const db = b.lastUpdatedAt || b.createdAt;
  if (moment(db).isAfter(da)) return 1;
  if (moment(da).isAfter(db)) return -1;
  return 0;
}
